/**
 * useTeacherProfile Hook
 * Manages teacher profile state and actions for the settings page.
 */
import { useState, useCallback, useEffect } from 'react';
import {
  getTeacherProfile,
  getTeacherProfileStatus,
  initializeTeacherProfile,
  updateTeacherProfile,
  addFeedbackExample,
  removeFeedbackExample,
  reExtractStyle,
} from '../services/memoryApi';
import type {
  TeacherProfile,
  ProfileStatus,
  UpdateProfileRequest,
} from '../types/memory';

interface UseTeacherProfileReturn {
  profile: TeacherProfile | null;
  status: ProfileStatus | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  fetchStatus: () => Promise<void>;
  initialize: (feedbackExamples: string[]) => Promise<void>;
  updateProfile: (updates: UpdateProfileRequest) => Promise<void>;
  addExample: (text: string, category?: string) => Promise<void>;
  removeExample: (index: number) => Promise<void>;
  reExtract: () => Promise<void>;
  clearError: () => void;
}

// Default teacher ID (will be replaced with auth later)
const DEFAULT_TEACHER_ID = 'teacher-1';

export function useTeacherProfile(
  teacherId: string = DEFAULT_TEACHER_ID
): UseTeacherProfileReturn {
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await getTeacherProfileStatus(teacherId);
      setStatus(result);
    } catch (err) {
      // Status check failing is not critical
      console.error('Failed to fetch profile status:', err);
    }
  }, [teacherId]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getTeacherProfile(teacherId);
      setProfile(result);
      setStatus({
        teacher_id: teacherId,
        has_profile: true,
        initialized_from: result.initialized_from,
        has_embedding: result.style_embedding !== null,
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // No profile exists yet
        setProfile(null);
        setStatus({
          teacher_id: teacherId,
          has_profile: false,
          initialized_from: null,
          has_embedding: false,
        });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch profile');
      }
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  const initialize = useCallback(
    async (feedbackExamples: string[]) => {
      setLoading(true);
      setError(null);

      try {
        await initializeTeacherProfile({
          teacher_id: teacherId,
          feedback_examples: feedbackExamples,
        });
        // Refresh profile after initialization
        await fetchProfile();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize profile');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [teacherId, fetchProfile]
  );

  const updateProfileData = useCallback(
    async (updates: UpdateProfileRequest) => {
      if (!profile) {
        setError('No profile to update');
        return;
      }

      // Optimistic update
      const previousProfile = profile;
      setProfile({
        ...profile,
        ...updates,
        // Handle array updates
        focus_areas: updates.focus_areas ?? profile.focus_areas,
        phrase_patterns: updates.phrase_patterns ?? profile.phrase_patterns,
        avoids: updates.avoids ?? profile.avoids,
      });

      try {
        const result = await updateTeacherProfile(teacherId, updates);
        setProfile(result);
      } catch (err) {
        // Rollback on error
        setProfile(previousProfile);
        setError(err instanceof Error ? err.message : 'Failed to update profile');
        throw err;
      }
    },
    [teacherId, profile]
  );

  const addExample = useCallback(
    async (text: string, category: string = 'general') => {
      setLoading(true);
      setError(null);

      try {
        await addFeedbackExample(teacherId, text, category);
        // Refresh profile to get updated corpus
        await fetchProfile();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add example');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [teacherId, fetchProfile]
  );

  const removeExample = useCallback(
    async (index: number) => {
      if (!profile) {
        setError('No profile to update');
        return;
      }

      // Optimistic update
      const previousExamples = profile.example_feedback;
      const updatedExamples = [...previousExamples];
      updatedExamples.splice(index, 1);
      setProfile({ ...profile, example_feedback: updatedExamples });

      try {
        await removeFeedbackExample(teacherId, index);
      } catch (err) {
        // Rollback on error
        setProfile({ ...profile, example_feedback: previousExamples });
        setError(err instanceof Error ? err.message : 'Failed to remove example');
        throw err;
      }
    },
    [teacherId, profile]
  );

  const reExtract = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await reExtractStyle(teacherId);
      // Refresh profile to get updated style
      await fetchProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-extract style');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [teacherId, fetchProfile]);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    status,
    loading,
    error,
    fetchProfile,
    fetchStatus,
    initialize,
    updateProfile: updateProfileData,
    addExample,
    removeExample,
    reExtract,
    clearError,
  };
}

export default useTeacherProfile;
